import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PainterMalmesbury() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const painter = {
    id: "painters-malmesbury",
    name: "Painters Malmesbury",
    location: "Malmesbury, Western Cape",
    rating: 4.7,
    reviews: 128,
    specialties: ["Interior Painting", "Exterior Painting", "Agricultural Buildings", "Commercial Properties"],
    image: "https://public.readdy.ai/ai/img_res/5e2ed8ca4a6eb46f6b426fca4e69e0e9.jpg",
    verified: true,
    yearsExperience: 12,
    website: "https://paintersmalmesbury.co.za",
    email: "info@paintersmalmesbury.co.za",
    phone: "+27 22 487 2156",
    description: "Professional painting services in Malmesbury and surrounding agricultural areas. We specialize in farm buildings, commercial properties, and residential homes in rural settings with expertise in weather-resistant finishes for agricultural environments.",
    services: [
      "Agricultural Building Painting",
      "Farm Structure Coating",
      "Residential Interior Painting",
      "Commercial Exterior Painting",
      "Roof Painting and Protection",
      "Waterproofing Systems",
      "Industrial Coating Solutions",
      "Maintenance Programs"
    ],
    serviceAreas: ["Malmesbury", "Swartland", "Darling", "Moorreesburg", "Riebeek Valley", "Agricultural Areas", "Rural Communities"],
    gallery: [
      "https://readdy.ai/api/search-image?query=Agricultural%20barn%20painting%20project%20in%20Malmesbury%2C%20professional%20painters%20working%20on%20farm%20building%20exterior%2C%20quality%20paint%20finish%20for%20rural%20structures%2C%20wheat%20farming%20landscape%2C%20organized%20work%20environment&width=400&height=300&seq=malmesbury-gallery-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Commercial%20building%20painting%20in%20Malmesbury%20town%20center%2C%20professional%20painters%20working%20on%20business%20premises%2C%20quality%20exterior%20finish%2C%20rural%20town%20setting%2C%20well-maintained%20commercial%20property&width=400&height=300&seq=malmesbury-gallery-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Residential%20home%20interior%20painting%20in%20Malmesbury%2C%20fresh%20painted%20living%20room%2C%20modern%20rural%20home%20design%2C%20quality%20paint%20finish%2C%20bright%20clean%20space%2C%20professional%20painting%20work&width=400&height=300&seq=malmesbury-gallery-3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Farm%20equipment%20shed%20painting%20project%20in%20Malmesbury%2C%20industrial%20coating%20application%2C%20agricultural%20machinery%20storage%2C%20protective%20paint%20systems%2C%20rural%20farming%20environment&width=400&height=300&seq=malmesbury-gallery-4&orientation=landscape"
    ],
    testimonials: [
      {
        name: "Johan van der Merwe",
        rating: 5,
        comment: "Excellent work on our farm buildings. They understand the unique requirements of agricultural properties and delivered outstanding results that protect our investment."
      },
      {
        name: "Sarah Botha",
        rating: 4,
        comment: "Professional service for our commercial property in Malmesbury. The team was reliable, efficient, and delivered quality work on schedule."
      },
      {
        name: "Pieter Steyn",
        rating: 5,
        comment: "Great painters for rural properties. They know how to handle the challenges of painting in agricultural environments and use the right materials for long-lasting results."
      }
    ]
  };

  useEffect(() => {
    document.title = "Painters Malmesbury | Professional House Painting Services Swartland | Residential Painters";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Painters Malmesbury provides professional painting services in Malmesbury, Swartland. Specializing in residential and farm property painting, interior and exterior work. Trusted local painting contractors.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Painters Malmesbury, house painters Malmesbury, painting contractors Swartland, residential painting, farm property painting');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-blue-600 to-blue-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20agricultural%20property%20in%20Malmesbury%20South%20Africa%2C%20rural%20farming%20setting%20with%20wheat%20fields%20and%20mountains%2C%20painters%20in%20clean%20uniforms%20with%20quality%20equipment%2C%20farm%20buildings%20and%20modern%20structures%2C%20bright%20natural%20lighting%2C%20high%20quality%20agricultural%20painting%20project&width=1200&height=600&seq=malmesbury-painters-hero&orientation=landscape)` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white">
            <div className="flex items-center mb-4">
              <h1 className="text-4xl md:text-5xl font-bold mr-4">{painter.name}</h1>
              {painter.verified && (
                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                  <i className="ri-shield-check-line mr-1"></i>
                  Verified
                </div>
              )}
            </div>
            
            <p className="text-xl mb-4 flex items-center">
              <i className="ri-map-pin-line mr-2"></i>
              {painter.location}
            </p>
            
            <div className="flex items-center space-x-6 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`ri-star-fill ${i < Math.floor(painter.rating) ? 'text-yellow-400' : 'text-gray-400'}`}></i>
                ))}
                <span className="ml-2">{painter.rating} ({painter.reviews} reviews)</span>
              </div>
              <span>{painter.yearsExperience} years experience</span>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={() => navigate('/quote')}
                className="bg-lime-400 text-black px-6 py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'services', label: 'Services' },
              { id: 'gallery', label: 'Gallery' },
              { id: 'reviews', label: 'Reviews' },
              { id: 'contact', label: 'Contact' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-lime-500 text-lime-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">About Painters Malmesbury - Your Trusted Swartland Painting Specialists</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Painters Malmesbury has been serving the Swartland region for over 12 years, establishing ourselves as the premier painting contractor for agricultural, commercial, and residential properties throughout Malmesbury and surrounding rural communities. Our deep understanding of the unique challenges faced by properties in agricultural environments, combined with our commitment to using the highest quality materials and proven techniques, makes us the trusted choice for property owners across the region.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Agricultural and Rural Property Specialists</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The Swartland region is renowned for its agricultural productivity, and we specialize in protecting and enhancing the buildings that support this vital industry. From grain silos and equipment sheds to farmhouses and worker accommodation, our team understands the specific requirements of agricultural structures. We use specialized coatings designed to withstand the harsh conditions of rural environments, including extreme temperature variations, dust exposure, and the corrosive effects of agricultural chemicals and fertilizers.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Commercial Property Excellence in Malmesbury</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  As the economic hub of the Swartland, Malmesbury hosts numerous commercial and industrial properties that require professional painting services. Our commercial painting division has extensive experience with retail establishments, office buildings, warehouses, and manufacturing facilities. We understand the importance of minimizing business disruption during painting projects and offer flexible scheduling options, including after-hours and weekend work, to ensure your operations continue smoothly while we enhance your property's appearance and protection.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Residential Painting Services for Rural Communities</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Rural homeowners face unique challenges when it comes to property maintenance, and our residential painting services are specifically designed to address these needs. We provide comprehensive interior and exterior painting services for homes throughout the Swartland, using premium paints that offer superior protection against dust, wind, and the intense UV radiation common in the region. Our color consultation services help homeowners choose palettes that complement the natural beauty of the rural landscape while providing practical benefits such as heat reflection and fade resistance.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Specialized Coating Systems for Agricultural Buildings</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Agricultural buildings require specialized coating systems that go beyond standard residential or commercial paints. Our agricultural coating services include anti-corrosion treatments for metal structures, moisture-resistant systems for grain storage facilities, and chemical-resistant coatings for areas exposed to fertilizers and pesticides. We work closely with farmers and agricultural managers to develop maintenance schedules that protect valuable infrastructure while minimizing interference with critical farming operations.
                </p>

                <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-8">Comprehensive Painting Solutions for the Swartland Region</h2>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Industrial and Infrastructure Painting</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The Swartland region's growing industrial sector requires specialized painting services that can handle large-scale projects and demanding environments. Our industrial painting capabilities include high-performance coatings for manufacturing equipment, protective systems for storage tanks and silos, and maintenance programs for infrastructure such as water towers and communication structures. We are fully equipped with the specialized equipment and safety protocols necessary for working on industrial projects, including aerial lifts, scaffolding systems, and confined space entry procedures.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Weather-Resistant Protection Systems</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The Swartland's Mediterranean climate presents specific challenges for painted surfaces, including intense summer heat, occasional winter rains, and strong berg winds. Our weather-resistant protection systems are specifically formulated to handle these conditions, incorporating UV-stable pigments, flexible resins that accommodate thermal expansion and contraction, and moisture management technologies that prevent water intrusion while allowing vapor transmission. These advanced systems ensure that your property maintains its appearance and protection year-round.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Preventive Maintenance Programs</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  For agricultural and commercial properties, regular maintenance is essential for protecting valuable investments and ensuring operational continuity. Our preventive maintenance programs provide scheduled inspections, touch-up services, and planned repainting cycles that keep your property in optimal condition while spreading costs over time. These programs are particularly valuable for large agricultural operations with multiple buildings and structures that require ongoing attention to maintain their protective coatings.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Environmental Stewardship and Safety</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  As a company serving agricultural communities, we are deeply committed to environmental stewardship and safety. We use low-VOC and environmentally friendly paints whenever possible, properly contain and dispose of all waste materials, and follow strict safety protocols to protect both our workers and the surrounding environment. Our team is trained in the safe handling of coatings and solvents, and we maintain comprehensive insurance coverage to protect our clients' interests.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Local Knowledge and Community Commitment</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Our team members are part of the Swartland community, and this local connection drives our commitment to excellence in every project. We understand the seasonal rhythms of agricultural life, the importance of reliable service in rural areas, and the value of building long-term relationships with our clients. Our local knowledge extends to understanding soil conditions that affect foundation moisture, prevailing wind patterns that influence paint application, and the specific challenges faced by different types of agricultural operations in the region.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Quality Assurance and Warranty Protection</h3>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Every project we complete is backed by comprehensive quality assurance procedures and warranty protection. Our quality control process includes material verification, application monitoring, and final inspection to ensure that all work meets our exacting standards. We provide detailed warranties on both materials and workmanship, and our local presence ensures that warranty service is readily available when needed. We maintain detailed records of all projects, including paint specifications and application conditions, to facilitate future maintenance and warranty claims.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {painter.specialties.map((specialty, index) => (
                        <span key={index} className="bg-lime-100 text-lime-800 px-3 py-1 rounded-full text-sm font-medium">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h3>
                    <div className="space-y-2">
                      {painter.serviceAreas.map((area, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <i className="ri-map-pin-line mr-2 text-lime-500"></i>
                          {area}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gray-50 p-6 rounded-lg sticky top-6">
                  <div className="mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20agricultural%20building%20in%20Malmesbury%20South%20Africa%2C%20rural%20property%20with%20farm%20setting%2C%20painters%20in%20uniform%20with%20quality%20equipment%2C%20traditional%20farm%20architecture%2C%20organized%20work%20site%20with%20countryside%20background&width=400&height=300&seq=malmesbury-sidebar-image&orientation=landscape"
                      alt="Professional painters Malmesbury agricultural building painting services South Africa rural properties"
                      className="w-full h-48 object-cover object-top rounded-lg"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Contact</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-mail-line mr-3 text-lime-500"></i>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-global-line mr-3 text-lime-500"></i>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        Visit Website
                      </a>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => navigate('/quote')}
                    className="w-full mt-6 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Get Free Quote
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Comprehensive Painting Services</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center mb-4">
                      <i className="ri-brush-line text-2xl text-lime-500 mr-3"></i>
                      <h3 className="text-lg font-semibold text-gray-900">{service}</h3>
                    </div>
                    <p className="text-gray-600">Professional {service.toLowerCase()} services with specialized materials for agricultural and rural environments.</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Project Gallery</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.gallery.map((image, index) => (
                  <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image} 
                      alt={`Painters Malmesbury agricultural painting project ${index + 1}`}
                      className="w-full h-64 object-cover object-top"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews</h2>
              <div className="space-y-6">
                {painter.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`ri-star-fill ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                        ))}
                      </div>
                      <span className="ml-3 font-semibold text-gray-900">{testimonial.name}</span>
                    </div>
                    <p className="text-gray-700">{testimonial.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-center">
                    <i className="ri-phone-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Phone</h3>
                      <a href={`tel:${painter.phone}`} className="text-gray-700 hover:text-lime-600">
                        {painter.phone}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-mail-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email</h3>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-global-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Website</h3>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        {painter.website}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-2xl text-lime-500 mr-4 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Service Area</h3>
                      <p className="text-gray-700">{painter.location} and surrounding Swartland region</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get a Quote</h3>
                <button 
                  onClick={() => navigate('/quote')}
                  className="w-full bg-lime-400 text-black py-4 rounded-lg font-semibold hover:bg-lime-500 transition-colors text-lg whitespace-nowrap cursor-pointer"
                >
                  Request Free Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
